// Application constants for maintainability and consistency

// Base URL for all shared links (always use production domain)
export const BASE_URL = "https://biozadania.com";

// History configuration
export const MAX_HISTORY_ITEMS = 20;
export const VISIBLE_HISTORY_ITEMS = 8;

// UI display limits
export const QUESTION_PREVIEW_LENGTH = 40;
export const TASKS_PER_PAGE = 50;

// Accuracy thresholds for visual feedback (percentages)
export const ACCURACY_THRESHOLD_HIGH = 70;
export const ACCURACY_THRESHOLD_MEDIUM = 40;

// Animation timing (milliseconds)
export const AUTO_ADVANCE_DELAY = 1200;
export const ANIMATION_STAGGER_DELAY = 0.03;
export const MAX_ANIMATED_ITEMS = 10;

// File size limits (bytes)
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB
export const COMPRESSION_TARGET_KB = 400;

// Form configuration
export const MAX_ANSWERS = 4;
export const TASK_NUMBER_MAX = 40;
export const SUBPOINT_MAX = 5;

// Pagination configuration
export const AUTO_LOAD_THRESHOLD = 2; // Load more tasks when <2 unsolved remain (aggressive background loading)

// List mode configuration
export const LIST_PAGE_SIZE = 10;
export const LIST_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

// Bilingual document types — tasks of these types support PL/EN translation toggle
export const BILINGUAL_DOCUMENT_TYPES = [
  "Matura Dwujęzyczna",
  "Informator Matury Dwujęzycznej",
] as const;

/** Returns true when the task's documentType is on the bilingual list. */
export function isBilingualTask(task: { documentType?: string | null } | null | undefined): boolean {
  if (!task?.documentType) return false;
  return (BILINGUAL_DOCUMENT_TYPES as readonly string[]).includes(task.documentType);
}

export type TaskLanguage = "pl" | "en";

/**
 * Picks the default language to display for a task.
 *
 * Bilingual matura convention: main fields hold the EN ORIGINAL,
 * *_pl fields hold the Polish TRANSLATION (may be missing).
 * - Non-bilingual → always "pl" (regular Polish content).
 * - Bilingual + Polish translation present → "pl" (preferred for student).
 * - Bilingual + no translation yet → "en" (show original).
 */
export function pickDefaultLanguage(task: any): TaskLanguage {
  if (!isBilingualTask(task)) return "pl";
  const s = (v: unknown) => typeof v === "string" && v.trim().length > 0;
  const a = (v: unknown) => Array.isArray(v) && v.some((x) => s(x));
  const o = (v: unknown) =>
    v !== null && v !== undefined &&
    (Array.isArray(v) ? v.length > 0 : typeof v === "object" && Object.keys(v as object).length > 0);

  const hasPL =
    s(task?.questionPl) || s(task?.instructionPl) || a(task?.answersPl) ||
    o(task?.tableDataPl) || o(task?.multiTableDataPl) || o(task?.matchingDataPl) ||
    s(task?.strikethroughTextPl);

  // EN original is always assumed present for bilingual tasks (main fields).
  return hasPL ? "pl" : "en";
}
